import React, { useEffect, useState, useMemo, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import { format, isWithinInterval } from "date-fns";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { useNavigate } from "react-router-dom";

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
`;

// ─── Cache (module-level, persists across tab navigation) ─────────────────────

let _cache = null; // { folders, agentList, passengerMap, paymentTotals, fetchedAt }

// ─── Status helpers ───────────────────────────────────────────────────────────

// function getStatus(folder) {
//   return folder.TotalPaid === 0 || folder.TotalPaid === "0.00"
//     ? "Dummy"
//     : "In Process";
// }

function getStatus(calculatedTotal) {
  return !calculatedTotal || calculatedTotal === 0
    ? "Dummy"
    : "In Process";
}

const STATUS_STYLES = {
  "In Process": { color: "#059669", bg: "#ecfdf5" },
  "Dummy":      { color: "#dc2626", bg: "#fef2f2" },
};

// ─── Component ────────────────────────────────────────────────────────────────

const SearchInvoice = () => {
  const [folders,         setFolders]         = useState([]);
  const [agentList,       setAgentList]       = useState([]);
  const [passengerMap,    setPassengerMap]    = useState({});
  const [paymentTotals,   setPaymentTotals]   = useState({});
  const [loading,         setLoading]         = useState(false);
  const [lastFetched,     setLastFetched]     = useState(null);
  const [refreshing,      setRefreshing]      = useState(false);

  // ── Filters ──
  const [selectedAgent,   setSelectedAgent]   = useState("");
  const [startDate,       setStartDate]       = useState("");
  const [endDate,         setEndDate]         = useState("");
  const [selectedStatus,  setSelectedStatus]  = useState("");
  const [passengerSearch, setPassengerSearch] = useState("");
  const [sortConfig,      setSortConfig]      = useState({ key: "FolderNo", dir: "asc" });

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = async (forceRefresh = false) => {
    if (_cache && !forceRefresh) {
      setFolders(_cache.folders);
      setAgentList(_cache.agentList);
      setPassengerMap(_cache.passengerMap);
      setPaymentTotals(_cache.paymentTotals);
      setLastFetched(_cache.fetchedAt);
      return;
    }

    forceRefresh ? setRefreshing(true) : setLoading(true);

    try {
      const [folderSnap, passengerSnap, paymentSnap] = await Promise.all([
        getDocs(collection(db, "FOLDERS")),
        getDocs(collection(db, "PassengerSegments")),
        getDocs(collection(db, "bankPayments")),
      ]);

      const folderData = folderSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const agents = [...new Set(folderData.map(f => f.Agent).filter(Boolean))].sort();

      const map = {};
      passengerSnap.docs.forEach(doc => {
        const p = doc.data();
        const folderNo = Number(p.FolderNo);
        const fullName = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ").toLowerCase();
        if (!map[folderNo]) map[folderNo] = [];
        map[folderNo].push(fullName);
      });

      // ── Build payment totals map from bankPayments ──
      // Key: folderNo as string → summed amount based on currency field
      const totalsMap = {};
      paymentSnap.docs.forEach(doc => {
        const p = doc.data();
        const folderNo = String(p.folderNo);
        const currency = (p.currency || "").toUpperCase();

        let amount = 0;
        if (currency === "GBP")      amount = Number(p.paymentGBP) || 0;
        else if (currency === "PKR") amount = Number(p.paymentPKR) || 0;
        else if (currency === "USD") amount = Number(p.paymentUSD) || 0;

        totalsMap[folderNo] = (totalsMap[folderNo] || 0) + amount;
      });

      const now = new Date();
      _cache = {
        folders: folderData,
        agentList: agents,
        passengerMap: map,
        paymentTotals: totalsMap,
        fetchedAt: now,
      };

      setFolders(folderData);
      setAgentList(agents);
      setPassengerMap(map);
      setPaymentTotals(totalsMap);
      setLastFetched(now);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const navigate = useNavigate();

  // ── Helpers ────────────────────────────────────────────────────────────────

  function GetCurrency(value) {
    if (!value) return "—";
    const v = value.toLowerCase();
    if (v === "tide")     return "GBP";
    if (v === "natwest")  return "GBP";
    if (v === "monzo")    return "GBP";
    if (v === "starlink") return "GBP";
    if (v === "starling") return "GBP";
    if (v === "allied")   return "PKR";
    if (v === "american") return "USD";
    return "—";
  }

  // ── Filter + Sort ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let data = [...folders];

    if (selectedAgent) data = data.filter(f => f.Agent === selectedAgent);

    if (startDate && endDate) {
      data = data.filter(f => {
        try {
          const d = new Date(f.CreatedAt);
          return isWithinInterval(d, { start: new Date(startDate), end: new Date(endDate) });
        } catch { return false; }
      });
    }

    // if (selectedStatus) data = data.filter(f => getStatus(f) === selectedStatus);

    if (selectedStatus) data = data.filter(f => {
  const paid = paymentTotals[String(f.FolderNo)];
  return getStatus(paid) === selectedStatus;
});

    if (passengerSearch.trim()) {
      const s = passengerSearch.toLowerCase();
      data = data.filter(f => (passengerMap[Number(f.FolderNo)] || []).some(n => n.includes(s)));
    }

    data.sort((a, b) => {
      let av = a[sortConfig.key], bv = b[sortConfig.key];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortConfig.dir === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.dir === "asc" ?  1 : -1;
      return 0;
    });

    return data;
  }, [folders, selectedAgent, startDate, endDate, selectedStatus, passengerSearch, passengerMap, sortConfig]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  // const stats = useMemo(() => ({
  //   total:     filtered.length,
  //   inProcess: filtered.filter(f => getStatus(f) === "In Process").length,
  //   dummy:     filtered.filter(f => getStatus(f) === "Dummy").length,
  // }), [filtered]);

  const stats = useMemo(() => ({
  total:     filtered.length,
  inProcess: filtered.filter(f => getStatus(paymentTotals[String(f.FolderNo)]) === "In Process").length,
  dummy:     filtered.filter(f => getStatus(paymentTotals[String(f.FolderNo)]) === "Dummy").length,
}), [filtered, paymentTotals]);

  const hasFilters = selectedAgent || startDate || endDate || selectedStatus || passengerSearch;
  const clearAll   = () => {
    setSelectedAgent(""); setStartDate(""); setEndDate("");
    setSelectedStatus(""); setPassengerSearch("");
  };

  const handleSort = key =>
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const SortArrow = ({ col }) => {
    if (sortConfig.key !== col) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ color: "#2563eb", marginLeft: 3 }}>{sortConfig.dir === "asc" ? "↑" : "↓"}</span>;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <GlobalStyle />
      <Page>

        {/* ── Header ── */}
        <PageHeader>
          <HeaderLeft>
            <PageLabel>Records</PageLabel>
            <PageTitle>Invoice Search</PageTitle>
            <PageSub>Browse and filter your folder records</PageSub>
          </HeaderLeft>
          <HeaderRight>
            <StatsRow>
              <StatChip $color="#2563eb">
                <span className="val">{stats.total}</span>
                <span className="lbl">Showing</span>
              </StatChip>
              <StatChip $color="#059669">
                <span className="val">{stats.inProcess}</span>
                <span className="lbl">Active</span>
              </StatChip>
              <StatChip $color="#dc2626">
                <span className="val">{stats.dummy}</span>
                <span className="lbl">Dummy</span>
              </StatChip>
            </StatsRow>
            <RefreshArea>
              {lastFetched && (
                <LastFetched>
                  Last fetched {format(lastFetched, "HH:mm")}
                </LastFetched>
              )}
              <RefreshBtn
                onClick={() => fetchData(true)}
                disabled={refreshing || loading}
                $spinning={refreshing}
              >
                <RefreshIcon $spinning={refreshing}>↻</RefreshIcon>
                {refreshing ? "Refreshing…" : "Refresh data"}
              </RefreshBtn>
            </RefreshArea>
          </HeaderRight>
        </PageHeader>

        {/* ── Filter Bar ── */}
        <FilterCard>
          <FilterRow>
            {/* Passenger search */}
            <SearchBox style={{ flex: 2 }}>
              <SearchIcon>⌕</SearchIcon>
              <input
                type="text"
                placeholder="Search passenger name…"
                value={passengerSearch}
                onChange={e => setPassengerSearch(e.target.value)}
              />
            </SearchBox>

            {/* Agent */}
            <FilterField>
              <FilterLabel>Agent</FilterLabel>
              <StyledSelect value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)}>
                <option value="">All agents</option>
                {agentList.map(a => <option key={a} value={a}>{a}</option>)}
              </StyledSelect>
            </FilterField>

            {/* Status */}
            <FilterField>
              <FilterLabel>Status</FilterLabel>
              <StyledSelect value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="In Process">In Process</option>
                <option value="Dummy">Dummy</option>
              </StyledSelect>
            </FilterField>

            {/* Date range */}
            <FilterField>
              <FilterLabel>From</FilterLabel>
              <StyledDateInput
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </FilterField>

            <FilterField>
              <FilterLabel>To</FilterLabel>
              <StyledDateInput
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </FilterField>

            {hasFilters && (
              <ClearBtn onClick={clearAll}>✕ Clear</ClearBtn>
            )}
          </FilterRow>
        </FilterCard>

        {/* ── Table ── */}
        <TableCard>
          <TableTopBar>
            <TableCount>
              {loading
                ? "Loading…"
                : `${filtered.length} folder${filtered.length !== 1 ? "s" : ""}`}
            </TableCount>
          </TableTopBar>

          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th className="sortable" onClick={() => handleSort("FolderNo")}>
                    Folder <SortArrow col="FolderNo" />
                  </th>
                  <th className="sortable" onClick={() => handleSort("Agent")}>
                    Agent <SortArrow col="Agent" />
                  </th>
                  <th>Currency</th>
                  <th>Total Paid</th>
                  <th className="sortable" onClick={() => handleSort("TotalQoute")}>
                    Total Quote <SortArrow col="TotalQoute" />
                  </th>
                  <th className="sortable" onClick={() => handleSort("CreatedAt")}>
                    Created At <SortArrow col="CreatedAt" />
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8}>
                      <LoadingRow>Loading invoice data…</LoadingRow>
                    </td>
                  </tr>
                )}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState>
                        <div className="icon">⊟</div>
                        <div className="msg">No folders found</div>
                        <div className="sub">
                          {hasFilters ? "Try adjusting your filters" : "No folder records available"}
                        </div>
                      </EmptyState>
                    </td>
                  </tr>
                )}

                {!loading && filtered.map((folder, i) => {
                  // const status = getStatus(folder);
                  const totalPaid = paymentTotals[String(folder.FolderNo)];
                  const status = getStatus(totalPaid);
                  const style  = STATUS_STYLES[status] || STATUS_STYLES["Dummy"];
                  let createdStr = "—";
                  try { createdStr = format(new Date(folder.CreatedAt), "dd MMM yyyy"); } catch {}

                  // ── Total Paid: summed from bankPayments by folderNo ──
                  const folderKey  = String(folder.FolderNo);
                  //  totalPaid  = paymentTotals[folderKey];
                  const currency   = GetCurrency(folder.bank);
                  const paidDisplay = totalPaid != null
                    ? `${totalPaid.toLocaleString()}`
                    : "—";

                  return (
                    <tr key={folder.id}>
                      <td className="muted center">{i + 1}</td>
                      <td>
                        <FolderChip
                          onClick={() => {
                            const url = `/FolderLedger?FolderNumber=${String(folder.FolderNo)}`;
                            window.open(url, "_blank");
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          #{folder.FolderNo}
                        </FolderChip>
                      </td>
                      <td>
                        <AgentCell>
                          <AgentAvatar>{(folder.Agent || "?")[0].toUpperCase()}</AgentAvatar>
                          <span>{folder.Agent || "—"}</span>
                        </AgentCell>
                      </td>
                      <td className="mono">{currency}</td>
                      <td className="mono">{paidDisplay}</td>
                      <td className="mono muted">{folder.TotalQoute || "—"}</td>
                      <td className="date">{createdStr}</td>
                      <td>
                        <StatusBadge $color={style.color} $bg={style.bg}>
                          <StatusDot $color={style.color} />
                          {status}
                        </StatusBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>

          {!loading && filtered.length > 0 && (
            <TableFooter>
              Showing <strong>{filtered.length}</strong> of <strong>{folders.length}</strong> records
            </TableFooter>
          )}
        </TableCard>

      </Page>
    </>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 32px 28px 80px;
  max-width: 1250px;
  font-family: 'DM Sans', sans-serif;
  animation: ${fadeUp} 0.35s ease both;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 18px;
  background: ${({ $color }) => $color}12;
  border: 1px solid ${({ $color }) => $color}30;
  border-radius: 10px;
  min-width: 70px;

  .val { font-size: 20px; font-weight: 600; color: ${({ $color }) => $color}; line-height: 1; }
  .lbl { font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: ${({ $color }) => $color}99; margin-top: 3px; }
`;

const RefreshArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LastFetched = styled.span`
  font-size: 11.5px;
  color: #94a3b8;
  font-family: 'DM Mono', monospace;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  transition: all 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #dbeafe; }
`;

const RefreshIcon = styled.span`
  font-size: 15px;
  display: inline-block;
  animation: ${({ $spinning }) => $spinning ? spin : "none"} 0.7s linear infinite;
`;

// ─── Filters ─────────────────────────────────────────────────────────────────

const FilterCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 200px;

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 12px 9px 34px;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    color: #0f172a;
    outline: none;
    transition: all 0.15s;
    &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb18; }
    &::placeholder { color: #cbd5e1; }
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
  pointer-events: none;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 140px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
`;

const sharedInput = `
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb18; }
`;

const StyledSelect = styled.select`
  ${sharedInput}
  padding-right: 28px;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  cursor: pointer;
  &:focus { background-color: white; }
`;

const StyledDateInput = styled.input`
  ${sharedInput}
`;

const ClearBtn = styled.button`
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  align-self: flex-end;
  transition: all 0.15s;
  &:hover { background: #fee2e2; }
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`;

const TableTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafafa;
`;

const TableCount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;

const TableWrap = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    min-width: 800px;
  }

  thead tr {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    padding: 10px 14px;
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
    user-select: none;
  }

  th.sortable {
    cursor: pointer;
    &:hover { color: #475569; }
  }

  td {
    padding: 11px 14px;
    border-bottom: 0.5px solid #f1f5f9;
    vertical-align: middle;
    color: #334155;
  }

  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #f8fafc; }

  .muted  { color: #94a3b8; }
  .center { text-align: center; }
  .mono   { font-family: 'DM Mono', monospace; font-size: 12.5px; }
  .date   { font-family: 'DM Mono', monospace; font-size: 12px; color: #475569; white-space: nowrap; }
`;

const FolderChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  background: #f1f5f9;
  border: 0.5px solid #e2e8f0;
  color: #475569;
`;

const AgentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AgentAvatar = styled.div`
  width: 24px; height: 24px;
  border-radius: 6px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const TableFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 12px;
  color: #94a3b8;
  font-family: 'DM Mono', monospace;
  text-align: right;

  strong { color: #64748b; font-weight: 500; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 56px 24px;
  .icon { font-size: 32px; margin-bottom: 10px; opacity: 0.3; }
  .msg  { font-size: 15px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 13px; color: #94a3b8; margin-top: 4px; }
`;

const LoadingRow = styled.div`
  text-align: center;
  padding: 48px;
  font-size: 13px;
  color: #94a3b8;
`;

export default SearchInvoice;